import { Injectable, OnDestroy } from "@angular/core";
import { of, Subject, throwError } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";
import { InsidePhotoDto } from "src/app/shared/Models/write/InsidePhotoDto";
import { OutsidePhotoDto } from "src/app/shared/Models/write/OutsidePhotoDto";
import { PhotoService, SurveyType } from "./photo.service";

@Injectable({
  providedIn: "root",
})
export class PhotoQueueService implements OnDestroy {
  private photoQueue: PhotoDto[] = [];
  private photosBeingDeleted: string[] = [];
  private failedPhotos: PhotoDto[] = [];
  private queue$ = new Subject<boolean>();
  private dispatcher = new Subject<PhotoDto>();

  queueRunning = false;

  get hasFailed() {
    return !this.queueRunning && this.failedPhotos.length > 0;
  }
  ngOnDestroy() {
    this.dispatcher.complete();
  }
  constructor(private _photoService: PhotoService) {
    this.dispatcher
      .pipe(
        switchMap((item) => {
          if (item.start) item.start();
          return this.saveByTypeSwitch(item.dto, item.type).pipe(
            tap(() => (item.success ? item.success() : undefined)),
            catchError((e) => {
              if (item.error) item.error(e);
              this.failedPhotos.push(item);
              return of(true);
            })
          );
        }),
        tap(() => {
          if (this.photoQueue.length > 0) {
            this.dispatcher.next(this.photoQueue.shift());
          } else {
            this.queueRunning = false;
            this.queue$.next(this.failedPhotos.length > 0);
            this.queue$.complete();
            this.queue$ = new Subject<boolean>();
          }
        })
      )
      .subscribe();
  }

  removePhoto(id: string, surveyType: SurveyType, removeOnApi: boolean) {
    var i = this.photoQueue.findIndex((p) => p.dto.id == id);
    var b = this.failedPhotos.findIndex((p) => p.dto.id == id);
    if (b >= 0) {
      this.failedPhotos.splice(i, 1);
      return of(true);
    }
    if (i >= 0) {
      this.photoQueue.splice(i, 1);
      return of(true);
    }
    // check to prevent double deleting same image ID wich creates a false state error.
    i = this.photosBeingDeleted.findIndex((p) => p == id);
    if (i >= 0) {
      this.photosBeingDeleted = [id];
      return of(true);
    }
    this.photosBeingDeleted = [id];
    if (removeOnApi) return this._photoService.removePhoto(id, surveyType);
    return of(true);
  }

  private startQueue(id?: string) {
    this.photoQueue.push(
      ...this.failedPhotos.splice(0, this.failedPhotos.length)
    );
    this.queueRunning = true;
    if (id) {
      var index = this.photoQueue.findIndex((v) => v.dto.id == id);
      this.dispatcher.next(this.photoQueue.splice(index, 1)[0]);
    } else {
      this.dispatcher.next(this.photoQueue.shift());
    }
  }

  retryFailed(id?: string) {
    if (!navigator.onLine) return this.processOffline();
    if (this.queueRunning) return this.queue$;
    if (this.photoQueue.length == 0 && this.failedPhotos.length == 0)
      return of(true);
    if (id) this.startQueue(id);
    else this.startQueue();

    return this.queue$;
  }

  savePhoto(
    data: OutsidePhotoDto | InsidePhotoDto,
    surveyType: SurveyType,
    start?: () => void,
    success?: () => void,
    error?: (e) => void
  ) {
    var dto = {
      dto: data,
      type: surveyType,
      start,
      success,
      error,
    };
    var index = this.photoQueue.findIndex((p) => p.dto.id == data.id);
    if (index != -1) this.photoQueue[index] = dto;
    else this.photoQueue.unshift(dto);
    if (!navigator.onLine) return this.processOffline();
    if (!this.queueRunning) this.startQueue();
    return this.queue$;
  }

  processOffline() {
    while (this.photoQueue.length > 0) {
      var p = this.photoQueue.shift();
      if (p.error) p.error(new Error("Offline"));
      this.failedPhotos.push(p);
    }
    return of(false);
  }

  saveByTypeSwitch(dto: OutsidePhotoDto | InsidePhotoDto, type: SurveyType) {
    if (!navigator.onLine) return throwError("Upload cancelled, not online.");
    return this._photoService.savePhoto(dto, type);
  }
}

export interface PhotoDto {
  dto: OutsidePhotoDto | InsidePhotoDto;
  type: SurveyType;
  start?: () => void;
  success?: () => void;
  error?: (e) => void;
}
